<!-- charts/RothRangeChart.vue -->
<template>
  <div class="roth-range-chart">
    <div class="chart-header">
      <h4>Conversion Amount vs. Net Benefit Analysis</h4>
      <div class="chart-controls">
        <div class="view-toggles">
          <button 
            @click="viewMode = 'benefit'"
            class="view-toggle"
            :class="{ active: viewMode === 'benefit' }"
          >
            Net Benefit
          </button>
          <button 
            @click="viewMode = 'tax'"
            class="view-toggle"
            :class="{ active: viewMode === 'tax' }"
          >
            Tax Cost
          </button>
          <button 
            @click="viewMode = 'rate'"
            class="view-toggle"
            :class="{ active: viewMode === 'rate' }"
          >
            Tax Rate
          </button>
        </div>
      </div>
    </div>

    <div class="chart-container">
      <HighChartsWrapper
        name="roth-range-analysis"
        chart-type="line"
        :chart-options="chartOptions"
        @seriesPointClick="handlePointClick"
      />
    </div>

    <div class="chart-insights">
      <div class="insight-item" :class="optimalInsightClass">
        <div class="insight-icon">🎯</div>
        <div class="insight-content">
          <strong>Optimal Conversion Amount</strong>
          <p>{{ formatCurrency(optimalPoint.amount) }} provides the highest net benefit of {{ formatCurrency(optimalPoint.netBenefit) }}</p>
        </div>
      </div>

      <div class="insight-item">
        <div class="insight-icon">💡</div>
        <div class="insight-content">
          <strong>Key Insight</strong>
          <p>{{ primaryInsight }}</p>
        </div>
      </div>

      <div v-if="bracketWarning" class="insight-item warning">
        <div class="insight-icon">⚠️</div>
        <div class="insight-content">
          <strong>Tax Bracket Impact</strong>
          <p>{{ bracketWarning }}</p>
        </div>
      </div>
    </div>

    <!-- Interactive Amount Selector -->
    <div class="amount-selector">
      <h5>Select Conversion Amount</h5>
      <div class="selector-container">
        <input
          v-model.number="selectedAmount"
          type="range"
          class="amount-slider"
          :min="0"
          :max="maxAmount"
          :step="5000"
          @input="updateSelectedPoint"
        />
        <div class="slider-labels">
          <span>$0</span>
          <span>{{ formatCurrency(maxAmount) }}</span>
        </div>
      </div>
      
      <div class="selected-point-details">
        <div class="detail-grid">
          <div class="detail-item">
            <span class="detail-label">Amount:</span>
            <span class="detail-value">{{ formatCurrency(selectedAmount) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Tax Cost:</span>
            <span class="detail-value">{{ formatCurrency(selectedPointData.taxCost) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Net Benefit:</span>
            <span class="detail-value" :class="selectedBenefitClass">{{ formatCurrency(selectedPointData.netBenefit) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Marginal Rate:</span>
            <span class="detail-value">{{ formatPercentage(selectedPointData.marginalRate) }}</span>
          </div>
        </div>
        
        <button 
          @click="selectThisAmount"
          class="btn btn-primary"
          :disabled="selectedAmount === currentAmount"
        >
          {{ selectedAmount === currentAmount ? 'Current Selection' : 'Use This Amount' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import HighChartsWrapper from '@frontend/ui/components/charts/HighChartsWrapper.vue';

export default {
  name: 'RothRangeChart',
  components: {
    HighChartsWrapper
  },
  props: {
    rangeData: {
      type: Array,
      required: true
    },
    currentAmount: {
      type: Number,
      required: true
    },
    formatCurrency: {
      type: Function,
      required: true
    }
  },
  emits: ['update-amount'],
  setup(props, { emit }) {
    const viewMode = ref('benefit');
    const selectedAmount = ref(props.currentAmount);

    // Watch for prop changes
    watch(() => props.currentAmount, (newAmount) => {
      selectedAmount.value = newAmount;
    });

    // Computed properties
    const maxAmount = computed(() => {
      return Math.max(...props.rangeData.map(d => d.conversionAmount));
    });

    const optimalPoint = computed(() => {
      return props.rangeData.reduce((optimal, current) => {
        return current.netBenefit > optimal.netBenefit ? current : optimal;
      }, props.rangeData[0]);
    });

    const selectedPointData = computed(() => {
      const closest = props.rangeData.reduce((closest, current) => {
        return Math.abs(current.conversionAmount - selectedAmount.value) < 
               Math.abs(closest.conversionAmount - selectedAmount.value) ? current : closest;
      }, props.rangeData[0]);
      
      return closest || { taxCost: 0, netBenefit: 0, marginalRate: 0 };
    });

    const selectedBenefitClass = computed(() => {
      return selectedPointData.value.netBenefit > 0 ? 'positive' : 'negative';
    });

    const optimalInsightClass = computed(() => {
      return optimalPoint.value.netBenefit > 0 ? 'optimal-good' : 'optimal-poor';
    });

    const primaryInsight = computed(() => {
      const optimal = optimalPoint.value;
      const current = props.rangeData.find(d => d.conversionAmount === props.currentAmount) || optimal;
      
      if (optimal.netBenefit <= 0) {
        return 'No conversion amount provides positive net benefit under current assumptions.';
      }
      
      if (Math.abs(optimal.conversionAmount - props.currentAmount) < 10000) {
        return 'Your current conversion amount is near optimal.';
      }
      
      if (optimal.conversionAmount > props.currentAmount) {
        return `Consider increasing conversion to ${props.formatCurrency(optimal.conversionAmount)} for maximum benefit.`;
      } else {
        return `Consider reducing conversion to ${props.formatCurrency(optimal.conversionAmount)} for better efficiency.`;
      }
    });

    const bracketWarning = computed(() => {
      const highMarginalRatePoint = props.rangeData.find(d => d.marginalRate > 0.30);
      if (!highMarginalRatePoint) return null;
      
      return `Conversions above ${props.formatCurrency(highMarginalRatePoint.conversionAmount)} face marginal rates above 30%, significantly reducing net benefit.`;
    });

    const chartOptions = computed(() => {
      let primarySeries, yAxisTitle, yAxisFormatter;
      
      switch (viewMode.value) {
        case 'benefit':
          primarySeries = {
            name: 'Net Benefit',
            data: props.rangeData.map(d => [d.conversionAmount, d.netBenefit]),
            color: '#28a745',
            zones: [
              { value: 0, color: '#dc3545' },
              { color: '#28a745' }
            ]
          };
          yAxisTitle = 'Net Benefit ($)';
          yAxisFormatter = function() { return props.formatCurrency(this.value); };
          break;
          
        case 'tax':
          primarySeries = {
            name: 'Tax Cost',
            data: props.rangeData.map(d => [d.conversionAmount, d.taxCost]),
            color: '#dc3545'
          };
          yAxisTitle = 'Tax Cost ($)';
          yAxisFormatter = function() { return props.formatCurrency(this.value); };
          break;
          
        case 'rate':
          primarySeries = {
            name: 'Marginal Tax Rate',
            data: props.rangeData.map(d => [d.conversionAmount, d.marginalRate * 100]),
            color: '#ffc107'
          };
          yAxisTitle = 'Marginal Tax Rate (%)';
          yAxisFormatter = function() { return this.value.toFixed(1) + '%'; };
          break;
      }

      return {
        chart: {
          type: 'line',
          height: 400,
          backgroundColor: 'transparent'
        },
        title: { text: null },
        xAxis: {
          title: { text: 'Conversion Amount ($)' },
          labels: {
            formatter: function() {
              return props.formatCurrency(this.value);
            }
          },
          plotLines: [
            {
              color: '#007bff',
              width: 2,
              value: props.currentAmount,
              dashStyle: 'Dash',
              label: {
                text: 'Current Amount',
                style: { color: '#007bff', fontWeight: 'bold' }
              }
            },
            {
              color: '#28a745',
              width: 2,
              value: optimalPoint.value.conversionAmount,
              dashStyle: 'Dot',
              label: {
                text: 'Optimal Amount',
                style: { color: '#28a745', fontWeight: 'bold' }
              }
            }
          ]
        },
        yAxis: {
          title: { text: yAxisTitle },
          labels: { formatter: yAxisFormatter },
          plotLines: viewMode.value === 'benefit' ? [
            {
              color: '#6c757d',
              width: 1,
              value: 0,
              dashStyle: 'Dash',
              label: {
                text: 'Break-even',
                style: { color: '#6c757d' }
              }
            }
          ] : []
        },
        tooltip: {
          shared: false,
          formatter: function() {
            const amount = props.formatCurrency(this.x);
            const value = viewMode.value === 'rate' ? 
              this.y.toFixed(1) + '%' : 
              props.formatCurrency(this.y);
            
            return `<b>${amount}</b><br/>${this.series.name}: <b>${value}</b>`;
          }
        },
        legend: { enabled: false },
        plotOptions: {
          line: {
            lineWidth: 3,
            marker: {
              enabled: false,
              states: {
                hover: { enabled: true, radius: 5 }
              }
            }
          }
        },
        series: [primarySeries]
      };
    });

    // Methods
    const handlePointClick = (pointData) => {
      const amount = pointData.value?.x || pointData.x;
      if (amount) {
        selectedAmount.value = amount;
        updateSelectedPoint();
      }
    };

    const updateSelectedPoint = () => {
      // Find closest data point
      const closest = props.rangeData.reduce((closest, current) => {
        return Math.abs(current.conversionAmount - selectedAmount.value) < 
               Math.abs(closest.conversionAmount - selectedAmount.value) ? current : closest;
      }, props.rangeData[0]);
      
      selectedAmount.value = closest.conversionAmount;
    };

    const selectThisAmount = () => {
      emit('update-amount', selectedAmount.value);
    };

    const formatPercentage = (value) => {
      return `${(value * 100).toFixed(1)}%`;
    };

    return {
      viewMode,
      selectedAmount,
      maxAmount,
      optimalPoint,
      selectedPointData,
      selectedBenefitClass,
      optimalInsightClass,
      primaryInsight,
      bracketWarning,
      chartOptions,
      handlePointClick,
      updateSelectedPoint,
      selectThisAmount,
      formatPercentage
    };
  }
};
</script>

<style scoped>
.roth-range-chart {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.chart-header h4 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.25rem;
}

.view-toggles {
  display: flex;
  gap: 0.5rem;
  background: #f1f3f4;
  padding: 0.25rem;
  border-radius: 6px;
}

.view-toggle {
  background: transparent;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  color: #6c757d;
  transition: all 0.2s ease;
}

.view-toggle:hover {
  background: #e9ecef;
  color: #495057;
}

.view-toggle.active {
  background: white;
  color: #2c3e50;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  font-weight: 600;
}

.chart-container {
  margin-bottom: 2rem;
  min-height: 400px;
}

/* Chart Insights */
.chart-insights {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
}

.insight-item {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  background: #f8f9fa;
}

.insight-item.optimal-good {
  border-color: #28a745;
  background: linear-gradient(to right, #f8fff9, #f8f9fa);
}

.insight-item.optimal-poor {
  border-color: #ffc107;
  background: linear-gradient(to right, #fffbf0, #f8f9fa);
}

.insight-item.warning {
  border-color: #dc3545;
  background: linear-gradient(to right, #fff5f5, #f8f9fa);
}

.insight-icon {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.insight-content strong {
  display: block;
  color: #2c3e50;
  margin-bottom: 0.25rem;
  font-size: 0.95rem;
}

.insight-content p {
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.4;
}

/* Amount Selector */
.amount-selector {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1.5rem;
}

.amount-selector h5 {
  margin: 0 0 1rem 0;
  color: #495057;
  font-size: 1.1rem;
}

.selector-container {
  margin-bottom: 1.5rem;
}

.amount-slider {
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: #e9ecef;
  outline: none;
  margin-bottom: 0.5rem;
  -webkit-appearance: none;
}

.amount-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #007bff;
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.amount-slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #007bff;
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.slider-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  color: #6c757d;
}

.selected-point-details {
  background: white;
  border: 1px solid #ced4da;
  border-radius: 6px;
  padding: 1rem;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 4px;
}

.detail-label {
  color: #6c757d;
  font-size: 0.9rem;
}

.detail-value {
  font-weight: 600;
  color: #2c3e50;
}

.detail-value.positive {
  color: #28a745;
}

.detail-value.negative {
  color: #dc3545;
}

.btn {
  width: 100%;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
  transform: translateY(-1px);
}

.btn-primary:disabled {
  background: #28a745;
}

/* Responsive */
@media (max-width: 768px) {
  .chart-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .view-toggles {
    justify-content: center;
  }
  
  .chart-insights {
    gap: 0.75rem;
  }
  
  .insight-item {
    gap: 0.75rem;
    padding: 0.75rem;
  }
  
  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>